import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  FaHeart, 
  FaUsers, 
  FaGlobe, 
  FaShieldAlt, 
  FaBolt,
  FaLightbulb,
  FaArrowRight,
  FaChartLine,
  FaCode,
  FaBrain,
  FaEye,
  FaTerminal,
  FaSearch,
  FaMicrochip,
  FaRocket,
  FaDownload
} from "react-icons/fa";
import Logo from "../components/Logo";
import Footer from "../components/Footer";
import "../styles/Donation.css";

function Donation() {
  useEffect(() => {
    // Clean up any existing BMC scripts and buttons first
    const existingScripts = document.querySelectorAll('script[src*="buymeacoffee"]');
    existingScripts.forEach(script => script.remove());
    
    const existingButtons = document.querySelectorAll('[id*="bmc"]');
    existingButtons.forEach(button => button.remove());
    
    // Load BuyMeACoffee script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://cdnjs.buymeacoffee.com/1.0.0/button.prod.min.js';
    script.setAttribute('data-name', 'bmc-button');
    script.setAttribute('data-slug', 'triagz');
    script.setAttribute('data-color', '#BD5FFF');
    script.setAttribute('data-emoji', '👨‍💻');
    script.setAttribute('data-font', 'Cookie');
    script.setAttribute('data-text', 'Support the development');
    script.setAttribute('data-outline-color', '#000000');
    script.setAttribute('data-font-color', '#ffffff');
    script.setAttribute('data-coffee-color', '#FFDD00');
    
    // Add script to document
    document.body.appendChild(script);
    
    // Hide manual button when BMC button loads successfully
    script.onload = () => {
      const manualButton = document.querySelector('.manual-donation-button');
      if (manualButton && document.querySelector('[id*="bmc"]')) {
        manualButton.style.display = 'none';
      }
    };
    
    return () => {
      // Cleanup scripts on unmount
      const scripts = document.querySelectorAll('script[src*="buymeacoffee"]');
      scripts.forEach(script => script.remove());
    };
  }, []);

  return (
    <div className="donation-container">
      <header className="donation-header">
                    <Logo size="small" showText={false} linkTo="/" />
        <nav className="nav-links">
          <Link to="/downloads" className="download-nav-link"><FaDownload /> Download</Link>
          <Link to="/login" className="login-link">Login</Link>
          <Link to="/register" className="register-link">Register</Link>
        </nav>
      </header>

      <main className="donation-main">
        {/* Hero Section */}
        <section className="donation-hero">
          <div className="hero-content">
            <div className="hero-badge">
              <FaHeart />
              <span>Support Development</span>
            </div>
            <h1>Help Us Build the Future of Security Research</h1>
            <p>
              Your support enables us to continue developing triagz and delivering cutting-edge 
              AI-powered security research platform to the community. Every contribution helps us 
              expand our capabilities and reach more security professionals worldwide.
            </p>
          </div>
          <div className="hero-visual">
            <div className="floating-cards">
              <div className="floating-card card-1">
                <FaHeart />
                <span>Community</span>
              </div>
              <div className="floating-card card-2">
                <FaRocket />
                <span>Innovation</span>
              </div>
              <div className="floating-card card-3">
                <FaBrain />
                <span>AI Research</span>
              </div>
            </div>
          </div>
        </section>

        {/* Donation Button Section */}
        <section className="donation-button-section">
          <div className="container">
            <div className="donation-button-content">
              <h2>Support Our Mission</h2>
              <p>
                Your donation directly supports the development of new features, 
                infrastructure improvements, and research into advanced AI based analysis capabilities.
              </p>
                             <div className="donation-button-wrapper">
                 {/* BuyMeACoffee button will be automatically rendered here by the script */}
                 <div className="manual-donation-button">
                   <a 
                     href="https://www.buymeacoffee.com/triagz" 
                     target="_blank" 
                     rel="noopener noreferrer"
                     className="donate-btn"
                   >
                     👨‍💻 Support the development
                   </a>
                 </div>
               </div>
            </div>
          </div>
        </section>

        {/* Why Donate Section */}
        <section className="why-donate-section">
          <div className="container">
            <div className="why-donate-content">
              <div className="why-donate-text">
                <h2>Why Your Support Matters</h2>
                <p>
                  triagz is more than just a security research platform—it's a vision of the future 
                  where AI empowers security professionals to work more efficiently and effectively. 
                  Your support helps us realize this vision and continue pushing the boundaries of 
                  what's possible in endpoint security research.
                </p>
                <Link to="/our-vision" className="vision-link">
                  <FaEye />
                  Learn More About Our Vision
                  <FaArrowRight />
                </Link>
              </div>
              <div className="why-donate-visual">
                <div className="impact-stats">
                  <div className="impact-stat">
                    <span className="impact-number">100%</span>
                    <span className="impact-label">Open Source</span>
                  </div>
                  <div className="impact-stat">
                    <span className="impact-number">24/7</span>
                    <span className="impact-label">Development</span>
                  </div>
                  <div className="impact-stat">
                    <span className="impact-number">∞</span>
                    <span className="impact-label">Possibilities</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Future Enhancements Section */}
        <section className="future-enhancements-section">
          <div className="container">
            <h2>Future Enhancements</h2>
            <p className="section-intro">
              You can donate to see future enhancement in the product including below things:
            </p>
            <div className="enhancements-grid">
              <div className="enhancement-card">
                <div className="enhancement-icon">
                  <FaGlobe />
                </div>
                <h3>Unified Endpoint Research Environment</h3>
                <p>
                  We're integrating a wide range of Management Control Protocols (MCPs) and 3rd party security products directly 
                  into the platform. No extra environment setup—connect once and manage everywhere.
                </p>
              </div>
              <div className="enhancement-card">
                <div className="enhancement-icon">
                  <FaBrain />
                </div>
                <h3>Tailored AI Intelligence</h3>
                <p>
                  Our fine-tuned, domain-specific AI models will deliver unmatched accuracy and 
                  relevance, outperforming any generic LLM for endpoint security tasks.
                </p>
              </div>
              <div className="enhancement-card">
                <div className="enhancement-icon">
                  <FaMicrochip />
                </div>
                <h3>Expanding Functionality</h3>
                <p>
                  Our goal is to create a one stop endpoint research agent which will take care of 
                  everything you want as security researcher. From process inspections to kernel 
                  interactions, you'll see new capabilities added continuously—because your analysis 
                  should never be limited by the tool.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How Donations Help Section */}
        <section className="donation-impact-section">
          <div className="container">
            <h2>How Your Donations Help</h2>
            <div className="impact-areas">
              <div className="impact-area">
                <div className="impact-icon">
                  <FaCode />
                </div>
                <h3>Development Resources</h3>
                <p>Fund full-time developers to accelerate feature development and bug fixes</p>
              </div>
              <div className="impact-area">
                <div className="impact-icon">
                  <FaShieldAlt />
                </div>
                <h3>Infrastructure Costs</h3>
                <p>Maintain reliable servers and cloud resources for our AI processing capabilities</p>
              </div>
                             <div className="impact-area">
                 <div className="impact-icon">
                   <FaSearch />
                 </div>
                 <h3>Research & Innovation</h3>
                 <p>Invest in cutting-edge AI research and experimental security analysis techniques</p>
               </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="donation-cta">
          <div className="container">
            <div className="cta-content">
              <h2>Join the Endpoint Research Revolution</h2>
              <p>
                Your support helps us build the tools that will define the future of endpoint 
                research and analysis. Together, we can make security analysis more accessible, 
                more powerful, and more effective for everyone.
              </p>
              <div className="cta-buttons">
                <Link to="/register" className="btn btn-primary">
                  Get Started <FaArrowRight />
                </Link>
                <Link to="/our-vision" className="btn btn-secondary">
                  Learn More
                </Link>
              </div>
            </div>
            <div className="cta-visual">
              <div className="network-nodes">
                <div className="node node-1"></div>
                <div className="node node-2"></div>
                <div className="node node-3"></div>
                <div className="node node-4"></div>
                <div className="node node-5"></div>
                <div className="connection connection-1"></div>
                <div className="connection connection-2"></div>
                <div className="connection connection-3"></div>
                <div className="connection connection-4"></div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default Donation; 